import { useEffect, useRef } from "react";
import { TIMING_CONFIG } from "@/config";

// Use centralized timing configuration
const POLLING_CONFIG = TIMING_CONFIG.THREAD_SYNC;

/**
 * Refresh thread list when thread ID changes.
 * Single responsibility: Keep thread list synchronized with current thread.
 *
 * Uses exponential backoff polling to ensure newly created threads are visible:
 * - Starts with a short delay (500ms)
 * - Doubles delay on each attempt until thread is found or max attempts reached
 * - Aborts polling when threadId changes (prevents stale updates)
 *
 * @param threadId - Current thread ID (triggers refresh when changed)
 * @param userId - Current user ID (prevents calls when user not loaded)
 * @param getThreads - Function to fetch threads from backend
 * @param setThreads - Function to update threads state
 */
export function useThreadListSync(
  threadId: string | null,
  userId: string | null,
  getThreads: () => Promise<any[]>,
  setThreads: (threads: any[]) => void,
): void {
  // Track if we're currently polling to prevent overlapping polls
  const isPollingRef = useRef(false);
  // Track the threadId we're polling for to detect changes
  const pollingForThreadRef = useRef<string | null>(null);

  useEffect(() => {
    if (!threadId || !userId) {
      return;
    }

    // Abort controller for cleanup
    const abortController = new AbortController();

    // Mark which thread we're polling for
    pollingForThreadRef.current = threadId;

    const pollForThread = async () => {
      // Prevent overlapping polls
      if (isPollingRef.current) {
        return;
      }

      isPollingRef.current = true;
      let attempts = 0;
      let delay: number = POLLING_CONFIG.INITIAL_DELAY_MS;

      try {
        while (
          attempts < POLLING_CONFIG.MAX_ATTEMPTS &&
          !abortController.signal.aborted
        ) {
          // Wait before fetching (exponential backoff)
          await new Promise((resolve) => {
            const timeoutId = setTimeout(resolve, delay);
            // Clear timeout on abort
            abortController.signal.addEventListener("abort", () => {
              clearTimeout(timeoutId);
              resolve(undefined);
            });
          });

          // Check if aborted or threadId changed during wait
          if (
            abortController.signal.aborted ||
            pollingForThreadRef.current !== threadId
          ) {
            break;
          }

          try {
            const threads = await getThreads();

            // Check again after async operation
            if (
              abortController.signal.aborted ||
              pollingForThreadRef.current !== threadId
            ) {
              break;
            }

            // Check if the new thread is in the list
            const threadFound = threads.some((t) => t.id === threadId);

            // Always update the thread list with latest data
            setThreads(threads);

            if (threadFound) {
              // Thread found, stop polling
              break;
            }

            // Thread not found yet, continue polling with backoff
            attempts++;
            delay = Math.min(
              delay * POLLING_CONFIG.BACKOFF_MULTIPLIER,
              POLLING_CONFIG.MAX_DELAY_MS
            );
          } catch (err) {
            // Log warning but continue polling
            console.warn(
              `[useThreadListSync] Poll attempt ${attempts + 1} failed:`,
              err instanceof Error ? err.message : err
            );
            attempts++;
            delay = Math.min(
              delay * POLLING_CONFIG.BACKOFF_MULTIPLIER,
              POLLING_CONFIG.MAX_DELAY_MS
            );
          }
        }
      } finally {
        isPollingRef.current = false;
      }
    };

    pollForThread();

    // Cleanup: abort polling when threadId changes or component unmounts
    return () => {
      abortController.abort();
    };
  }, [threadId, userId, getThreads, setThreads]);
}
